"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { TRADE_CATEGORIES } from "@/lib/trades";

/* ── Trade grouping ────────────────────────────────────────────── */

interface TradeGroup {
  label: string;
  slugs: string[];
}

const TRADE_GROUPS: TradeGroup[] = [
  {
    label: "Common Trades",
    slugs: [
      "plumber",
      "electrician",
      "builder",
      "carpenter",
      "roofer",
      "painter-decorator",
      "plasterer",
      "handyman",
    ],
  },
  {
    label: "Kitchen & Bathroom",
    slugs: ["kitchen-fitter", "bathroom-fitter", "tiler"],
  },
  {
    label: "Heating & Gas",
    slugs: [
      "gas-engineer",
      "heating-engineer",
      "heat-pump-installer",
    ],
  },
  {
    label: "Electrical & Tech",
    slugs: [
      "ev-charger-installer",
      "alarm-security-installer",
      "aerial-satellite-installer",
      "solar-panel-installer",
    ],
  },
  {
    label: "Outdoor & Garden",
    slugs: [
      "landscaper",
      "gardener",
      "fencing-specialist",
      "driveway-specialist",
      "paving-specialist",
      "groundworker",
      "tree-surgeon",
    ],
  },
  {
    label: "Structural & Building",
    slugs: [
      "bricklayer",
      "scaffolder",
      "rendering-specialist",
      "loft-conversion-specialist",
      "garage-door-specialist",
      "conservatory-specialist",
      "damp-proofing-specialist",
      "insulation-specialist",
    ],
  },
  {
    label: "Flooring & Interior",
    slugs: ["flooring-specialist", "window-fitter"],
  },
  {
    label: "Professional Services",
    slugs: ["architect", "surveyor"],
  },
  {
    label: "Specialist",
    slugs: [
      "locksmith",
      "chimney-sweep",
      "pest-control",
      "cleaner",
      "removals",
      "drainage-specialist",
      "appliance-repair",
      "skip-hire",
    ],
  },
];

// Build a slug -> name lookup
const TRADE_MAP = new Map<string, string>(
  TRADE_CATEGORIES.map((t) => [t.slug, t.name])
);

/* ── Component ─────────────────────────────────────────────────── */

interface TradePickerProps {
  /** Currently selected trade slug (empty string or undefined = nothing selected) */
  value?: string;
  /** Fires with the selected trade slug, or "" when cleared */
  onChange?: (slug: string) => void;
  /** Placeholder shown when nothing is selected */
  placeholder?: string;
  /** HTML name attribute — a hidden input is rendered so native forms work */
  name?: string;
  /** Label for screen readers */
  ariaLabel?: string;
  /** Extra classes on the outer wrapper */
  className?: string;
}

export function TradePicker({
  value = "",
  onChange,
  placeholder = "Select trade",
  name,
  ariaLabel = "Trade category",
  className = "",
}: TradePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedName = value ? (TRADE_MAP.get(value) ?? value) : "";

  // ── Filtered list ──────────────────────────────────────────────
  const query = search.toLowerCase().trim();

  // Flat filtered list for search mode
  const flatFiltered = query
    ? TRADE_CATEGORIES.filter((t) =>
        t.name.toLowerCase().includes(query)
      )
    : [];

  // Grouped list for browse mode (no search)
  const groupedFiltered = !query
    ? TRADE_GROUPS.map((group) => ({
        ...group,
        trades: group.slugs
          .map((slug) => {
            const name = TRADE_MAP.get(slug);
            return name ? { slug, name } : null;
          })
          .filter(Boolean) as { slug: string; name: string }[],
      })).filter((g) => g.trades.length > 0)
    : [];

  // Build a flat selectable item list for keyboard navigation
  const selectableItems: { slug: string; name: string }[] = query
    ? flatFiltered
    : groupedFiltered.flatMap((g) => g.trades);

  // ── Handlers ───────────────────────────────────────────────────
  const openPanel = useCallback(() => {
    setOpen(true);
    setActiveIndex(-1);
    // Focus the search input after panel opens
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setSearch("");
    setActiveIndex(-1);
  }, []);

  function selectTrade(slug: string) {
    onChange?.(slug);
    closePanel();
  }

  function clearSelection(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    onChange?.("");
    closePanel();
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        closePanel();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, closePanel]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openPanel();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < selectableItems.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : selectableItems.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && selectableItems[activeIndex]) {
          selectTrade(selectableItems[activeIndex].slug);
        }
        break;
      case "Escape":
        e.preventDefault();
        closePanel();
        break;
    }
  }

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Hidden input for native form submission */}
      {name && <input type="hidden" name={name} value={value} />}

      {/* Trigger row: trigger button + sibling clear button (clear is NOT nested
          inside the trigger button, so it has a valid tab order and is keyboard
          operable). Visual order matches the original: label, clear, chevron. */}
      <div className="flex items-center w-full h-full gap-2">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => (open ? closePanel() : openPanel())}
          aria-label={`${selectedName || placeholder}, ${ariaLabel}`}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="flex items-center flex-1 min-w-0 h-full text-left cursor-pointer bg-transparent border-none p-0 m-0 outline-none"
        >
          <span
            className={`flex-1 truncate ${selectedName ? "text-charcoal" : "text-text-muted"}`}
          >
            {selectedName || placeholder}
          </span>
        </button>
        {value && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={clearSelection}
            className="shrink-0 w-5 h-5 rounded-full bg-stone/40 flex items-center justify-center hover:bg-stone/60 transition-colors cursor-pointer border-none p-0"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M2 2l8 8M10 2l-8 8" />
            </svg>
          </button>
        )}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          onClick={() => (open ? closePanel() : openPanel())}
          className={`shrink-0 text-text-muted transition-transform duration-150 cursor-pointer ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] max-h-[min(70vh,420px)] flex flex-col overflow-hidden animate-fade-in">
          {/* Search field */}
          <div className="p-3 bg-cream/40">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              placeholder="Search trades..."
              aria-label="Search trades"
              className="w-full px-3 py-2.5 bg-white rounded-md text-sm text-charcoal placeholder:text-text-muted outline-none border-none focus:ring-2 focus:ring-forest/20"
            />
          </div>

          {/* Option list */}
          <div
            ref={listRef}
            role="listbox"
            aria-label="Trade categories"
            className="flex-1 overflow-y-auto overscroll-contain py-1"
          >
            {query ? (
              // ── Search mode: flat filtered list ──
              flatFiltered.length === 0 ? (
                <p className="text-sm text-text-muted px-4 py-6 text-center">
                  No trades matching &ldquo;{search}&rdquo;
                </p>
              ) : (
                flatFiltered.map((trade, i) => {
                  const isActive = activeIndex === i;
                  const isSelected = trade.slug === value;
                  return (
                    <button
                      key={trade.slug}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-index={i}
                      onClick={() => selectTrade(trade.slug)}
                      className={`flex items-center w-full text-left px-4 py-2.5 text-sm transition-colors border-none cursor-pointer ${
                        isActive
                          ? "bg-sage-pale"
                          : "bg-transparent hover:bg-warm-bg"
                      } ${isSelected ? "text-forest font-semibold" : "text-charcoal"}`}
                    >
                      {trade.name}
                      {isSelected && (
                        <svg
                          className="ml-auto shrink-0 text-forest"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  );
                })
              )
            ) : (
              // ── Browse mode: grouped list ──
              groupedFiltered.map((group) => {
                // Calculate the global flat index offset for this group
                let groupOffset = 0;
                for (const g of groupedFiltered) {
                  if (g.label === group.label) break;
                  groupOffset += g.trades.length;
                }

                return (
                  <div key={group.label} className="mb-1">
                    <div className="px-4 pt-3 pb-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-text-muted">
                        {group.label}
                      </span>
                    </div>
                    {group.trades.map((trade, j) => {
                      const flatIndex = groupOffset + j;
                      const isActive = activeIndex === flatIndex;
                      const isSelected = trade.slug === value;
                      return (
                        <button
                          key={trade.slug}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          data-index={flatIndex}
                          onClick={() => selectTrade(trade.slug)}
                          className={`flex items-center w-full text-left px-4 py-2.5 text-sm transition-colors border-none cursor-pointer ${
                            isActive
                              ? "bg-sage-pale"
                              : "bg-transparent hover:bg-warm-bg"
                          } ${isSelected ? "text-forest font-semibold" : "text-charcoal"}`}
                        >
                          {trade.name}
                          {isSelected && (
                            <svg
                              className="ml-auto shrink-0 text-forest"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
